let vimeoSdkPromise = null;

function loadVimeoSdk() {
  if (window.Vimeo && window.Vimeo.Player) return Promise.resolve(window.Vimeo);
  if (vimeoSdkPromise) return vimeoSdkPromise;

  vimeoSdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.onload = () => resolve(window.Vimeo);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return vimeoSdkPromise;
}

// Both adapters expose the same promise-based API (modelled on the Vimeo SDK) so
// the controls don't need to know which kind of player they're driving.
class VimeoPlayerAdapter {
  constructor(Vimeo, iframe) {
    this.player = new Vimeo.Player(iframe);
    // The iframe is loaded with muted=1 so autoplay works on mobile.
    this.player.setMuted(true).catch(() => {});
  }

  on(event, callback) {
    if (event === 'volumechange') {
      this.player.on('volumechange', (data) => callback(data.volume === 0));
    } else {
      this.player.on(event, callback);
    }
  }

  getDuration() {
    return this.player.getDuration();
  }

  getPaused() {
    return this.player.getPaused();
  }

  getMuted() {
    return Promise.resolve(true);
  }

  play() {
    return this.player.play();
  }

  pause() {
    return this.player.pause();
  }

  setCurrentTime(seconds) {
    return this.player.setCurrentTime(seconds);
  }

  setMuted(muted) {
    return this.player.setVolume(muted ? 0 : 1);
  }
}

class NativePlayerAdapter {
  constructor(video) {
    this.video = video;
  }

  on(event, callback) {
    const video = this.video;
    if (event === 'timeupdate') {
      video.addEventListener('timeupdate', () => callback({ seconds: video.currentTime, duration: video.duration }));
    } else if (event === 'volumechange') {
      video.addEventListener('volumechange', () => callback(video.muted || video.volume === 0));
    } else if (event === 'error') {
      video.addEventListener('error', () => callback(video.error));
    } else {
      video.addEventListener(event, callback);
    }
  }

  getDuration() {
    const video = this.video;
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) return Promise.resolve(video.duration);
    return new Promise((resolve) => {
      video.addEventListener('loadedmetadata', () => resolve(video.duration), { once: true });
    });
  }

  getPaused() {
    return Promise.resolve(this.video.paused);
  }

  getMuted() {
    return Promise.resolve(this.video.muted || this.video.volume === 0);
  }

  play() {
    return this.video.play();
  }

  pause() {
    this.video.pause();
    return Promise.resolve();
  }

  setCurrentTime(seconds) {
    this.video.currentTime = seconds;
    return Promise.resolve();
  }

  setMuted(muted) {
    this.video.muted = muted;
    if (!muted && this.video.volume === 0) this.video.volume = 1;
    return Promise.resolve();
  }
}

class VideoCustomControls {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.host = wrapper.dataset.customControls;
    this.controls = wrapper.querySelector('[data-vimeo-controls]');
    if (!this.controls) return;

    this.player = null;
    this.pendingPlayIntent = null;
    this.stateConfirmed = false;
    this.duration = 0;
    this.isSeeking = false;
    this.isFakeFullscreen = false;
    this.supportsFullscreen = Boolean(
      wrapper.requestFullscreen || wrapper.webkitRequestFullscreen
    );

    this.playPauseButton = this.controls.querySelector('[data-vimeo-toggle-play]');
    this.muteButton = this.controls.querySelector('[data-vimeo-toggle-mute]');
    this.fullscreenButton = this.controls.querySelector('[data-vimeo-toggle-fullscreen]');
    this.progress = this.controls.querySelector('[data-vimeo-progress]');
    this.progressFill = this.controls.querySelector('[data-vimeo-progress-fill]');
    this.currentTimeEl = this.controls.querySelector('[data-vimeo-current-time]');
    this.durationEl = this.controls.querySelector('[data-vimeo-duration]');

    this.onFullscreenChange = this.onFullscreenChange.bind(this);

    this.observer = new MutationObserver(() => this.findMedia());
    this.observer.observe(this.wrapper, { childList: true });
    this.findMedia();

    // Blocks can be wrapped in a link: don't let interacting with the controls navigate.
    this.controls.addEventListener('click', (event) => event.preventDefault());

    this.playPauseButton.addEventListener('click', () => this.togglePlay());
    this.muteButton.addEventListener('click', () => this.toggleMute());
    this.fullscreenButton.addEventListener('click', () => this.toggleFullscreen());
    this.progress.addEventListener('pointerdown', (event) => this.startSeek(event));
    this.progress.addEventListener('keydown', (event) => this.onProgressKeydown(event));

    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
  }

  findMedia() {
    if (this.player) return;

    if (this.host === 'native') {
      const video = this.wrapper.querySelector('video');
      if (!video) return;

      this.observer.disconnect();
      // Without native controls, clicking the video itself should still toggle playback
      // (unless the video is inside a link, where the click should navigate).
      if (!video.closest('a')) video.addEventListener('click', () => this.togglePlay());
      this.initPlayer(new NativePlayerAdapter(video));
      return;
    }

    const iframe = this.wrapper.querySelector('iframe.js-vimeo');
    if (!iframe) return;

    this.observer.disconnect();
    // The iframe swallows clicks, so cover it with a layer that toggles playback
    // (unless the video is inside a link, where the click should navigate).
    if (!iframe.closest('a')) {
      const clickLayer = document.createElement('div');
      clickLayer.className = 'vimeo-controls__click-layer';
      clickLayer.style.cssText = 'position:absolute;inset:0;z-index:1;cursor:pointer;display:block;';
      clickLayer.addEventListener('click', () => this.togglePlay());
      this.wrapper.insertBefore(clickLayer, this.controls);
    }
    loadVimeoSdk()
      .then((Vimeo) => {
        const adapter = new VimeoPlayerAdapter(Vimeo, iframe);
        this.initPlayer(adapter);
        // Vimeo letterboxes videos whose ratio differs from the iframe's, so fit the
        // controls to the visible picture instead of the whole iframe.
        Promise.all([adapter.player.getVideoWidth(), adapter.player.getVideoHeight()])
          .then(([width, height]) => {
            if (!width || !height) return;
            this.videoRatio = width / height;
            this.fitControlsToVideo(iframe);
            new ResizeObserver(() => this.fitControlsToVideo(iframe)).observe(iframe);
          })
          .catch(() => {});
      })
      .catch(() => {});
  }

  fitControlsToVideo(iframe) {
    const frameWidth = iframe.offsetWidth;
    const frameHeight = iframe.offsetHeight;
    if (!frameWidth || !frameHeight) return;

    const visibleWidth = Math.min(frameWidth, frameHeight * this.videoRatio);
    const visibleHeight = Math.min(frameHeight, frameWidth / this.videoRatio);
    const sideGap = (frameWidth - visibleWidth) / 2;
    const bottomGap = (frameHeight - visibleHeight) / 2;
    const wrapperWidth = this.wrapper.clientWidth;
    const wrapperHeight = this.wrapper.clientHeight;

    this.controls.style.left = `${iframe.offsetLeft + sideGap}px`;
    this.controls.style.right = `${wrapperWidth - iframe.offsetLeft - frameWidth + sideGap}px`;
    this.controls.style.bottom = `${wrapperHeight - iframe.offsetTop - frameHeight + bottomGap}px`;
  }

  initPlayer(player) {
    this.player = player;

    this.player.on('play', () => this.setPlayingState(true));
    this.player.on('pause', () => this.setPlayingState(false));
    this.player.on('timeupdate', (data) => this.updateProgress(data.seconds, data.duration));
    this.player.on('volumechange', (isMuted) => this.setMutedState(isMuted));
    this.player.on('error', (error) => {
      // Surface it instead of failing silently — e.g. a Vimeo PrivacyError means this
      // video's domain-embed restrictions don't allow the current domain to play it.
      console.error('Video player error:', error);
    });

    this.player
      .getMuted()
      .then((isMuted) => this.setMutedState(isMuted))
      .catch(() => {});

    Promise.all([this.player.getDuration(), this.player.getPaused()])
      .then(([duration, paused]) => {
        this.duration = duration;
        this.durationEl.textContent = this.formatTime(duration);
        if (this.pendingPlayIntent === null) this.setPlayingState(!paused);
        this.stateConfirmed = true;
      })
      .catch(() => {});

    this.controls.hidden = false;

    if (this.pendingPlayIntent !== null) {
      const intent = this.pendingPlayIntent;
      this.pendingPlayIntent = null;
      this.stateConfirmed = true;
      this.setPlayingState(intent);
      (intent ? this.player.play() : this.player.pause()).catch((error) => {
        console.error('Video play/pause failed:', error);
        this.setPlayingState(!intent);
      });
    }
  }

  togglePlay() {
    if (!this.player) {
      // SDK is still loading (common on slow mobile connections): remember what
      // the user wants and apply it as soon as the player becomes ready.
      this.pendingPlayIntent = !(this.pendingPlayIntent ?? this.isPlayingAssumed());
      this.setPlayingState(this.pendingPlayIntent);
      return;
    }

    if (!this.stateConfirmed) {
      // Mobile browsers don't always honor autoplay even when muted, so our
      // assumed "playing" default could be wrong. Ask the player for the truth
      // once before deciding what this tap should do, instead of guessing and
      // needing a second tap to correct it.
      this.stateConfirmed = true;
      this.player
        .getPaused()
        .then((isPaused) => {
          this.setPlayingState(!isPaused);
          return isPaused
            ? this.player.play().catch((error) => {
                console.error('Video play failed:', error);
                this.setPlayingState(false);
              })
            : this.player.pause().catch((error) => {
                console.error('Video pause failed:', error);
                this.setPlayingState(true);
              });
        })
        .catch(() => {});
      return;
    }

    // Once confirmed, decide from our own tracked state and act immediately —
    // it's kept accurate by the player's real 'play'/'pause' events.
    const wantsToPlay = !this.isPlayingAssumed();
    this.setPlayingState(wantsToPlay);
    (wantsToPlay ? this.player.play() : this.player.pause()).catch((error) => {
      console.error('Video play/pause failed:', error);
      // It didn't actually happen — undo the optimistic UI update.
      this.setPlayingState(!wantsToPlay);
    });
  }

  isPlayingAssumed() {
    return !this.playPauseButton.classList.contains('vimeo-controls__play-pause--paused');
  }

  setPlayingState(isPlaying) {
    this.playPauseButton.classList.toggle('vimeo-controls__play-pause--paused', !isPlaying);
    this.playPauseButton.setAttribute('aria-pressed', String(isPlaying));
    this.playPauseButton.setAttribute(
      'aria-label',
      isPlaying ? this.playPauseButton.dataset.pauseLabel : this.playPauseButton.dataset.playLabel
    );
  }

  toggleMute() {
    if (!this.player) return;
    const isMuted = this.muteButton.classList.contains('vimeo-controls__mute--muted');
    this.player.setMuted(!isMuted).catch(() => {});
    this.setMutedState(!isMuted);
  }

  setMutedState(isMuted) {
    this.muteButton.classList.toggle('vimeo-controls__mute--muted', isMuted);
    this.muteButton.setAttribute('aria-pressed', String(isMuted));
    this.muteButton.setAttribute(
      'aria-label',
      isMuted ? this.muteButton.dataset.unmuteLabel : this.muteButton.dataset.muteLabel
    );
  }

  toggleFullscreen() {
    if (!this.supportsFullscreen) {
      this.toggleFakeFullscreen();
      return;
    }

    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } else if (this.wrapper.requestFullscreen) {
      this.wrapper.requestFullscreen();
    } else if (this.wrapper.webkitRequestFullscreen) {
      this.wrapper.webkitRequestFullscreen();
    }
  }

  toggleFakeFullscreen() {
    this.isFakeFullscreen = !this.isFakeFullscreen;
    this.wrapper.classList.toggle('vimeo-fake-fullscreen', this.isFakeFullscreen);
    this.setFullscreenState(this.isFakeFullscreen);
  }

  onFullscreenChange() {
    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
    this.setFullscreenState(Boolean(fullscreenElement) && fullscreenElement === this.wrapper);
  }

  setFullscreenState(isFullscreen) {
    this.fullscreenButton.classList.toggle('vimeo-controls__fullscreen--active', isFullscreen);
    this.fullscreenButton.setAttribute(
      'aria-label',
      isFullscreen ? this.fullscreenButton.dataset.exitLabel : this.fullscreenButton.dataset.enterLabel
    );
  }

  updateProgress(seconds, duration) {
    if (this.isSeeking) return;
    if (duration) this.duration = duration;
    const percent = this.duration ? (seconds / this.duration) * 100 : 0;
    this.progressFill.style.width = `${percent}%`;
    this.progress.setAttribute('aria-valuenow', String(Math.round(percent)));
    this.currentTimeEl.textContent = this.formatTime(seconds);
  }

  startSeek(event) {
    event.preventDefault();
    this.isSeeking = true;

    const seekToPointer = (pointerEvent) => {
      const rect = this.progress.getBoundingClientRect();
      const ratio = Math.min(Math.max((pointerEvent.clientX - rect.left) / rect.width, 0), 1);
      this.progressFill.style.width = `${ratio * 100}%`;
      this.progress.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
      return ratio;
    };

    seekToPointer(event);

    const onMove = (moveEvent) => seekToPointer(moveEvent);
    const onUp = (upEvent) => {
      const ratio = seekToPointer(upEvent);
      if (this.player) this.player.setCurrentTime(ratio * this.duration).catch(() => {});
      this.isSeeking = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  onProgressKeydown(event) {
    const step = 5;
    let seconds = null;

    if (event.key === 'ArrowRight') seconds = Math.min(this.duration, this.getCurrentSeconds() + step);
    else if (event.key === 'ArrowLeft') seconds = Math.max(0, this.getCurrentSeconds() - step);
    else if (event.key === 'Home') seconds = 0;
    else if (event.key === 'End') seconds = this.duration;
    else return;

    event.preventDefault();
    if (this.player) this.player.setCurrentTime(seconds).catch(() => {});
    this.updateProgress(seconds, this.duration);
  }

  getCurrentSeconds() {
    const percent = Number(this.progress.getAttribute('aria-valuenow')) || 0;
    return (percent / 100) * this.duration;
  }

  formatTime(seconds) {
    const total = Math.max(0, Math.floor(seconds || 0));
    const minutes = Math.floor(total / 60);
    const secs = String(total % 60).padStart(2, '0');
    return `${minutes}:${secs}`;
  }
}

function initVideoCustomControls() {
  const wrappers = document.querySelectorAll('[data-custom-controls]');
  if (!wrappers.length) return;

  // Start fetching the SDK right away instead of waiting for the poster to be
  // tapped, so it's already available by the time the user interacts on mobile.
  if (document.querySelector('[data-custom-controls="vimeo"]')) loadVimeoSdk().catch(() => {});

  wrappers.forEach((wrapper) => {
    if (wrapper.videoControls) return;
    wrapper.videoControls = new VideoCustomControls(wrapper);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoCustomControls);
} else {
  initVideoCustomControls();
}
